import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Label,
  FormGroup,
  Button,
  Badge
} from 'reactstrap';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageDataLoader from '../Utility/PageDataLoader';
import Paginator from '../Utility/Paginator';
import SweetAlert from 'react-bootstrap-sweetalert';
import { fetchParentQueries } from '../../api/parentQuery';
import moment from 'moment';
import { validatePermissionKey } from '../../helpers/permission';
import {
  ANSWER_PARENT_QUERY,
  PUBLISH_PARENT_QUERY
} from '../Utility/PermissionKeys';
import ViewQueryModal from './ViewQueryModal';
import AnswerModal from './AnswerModal';
import ConfirmModal from './ConfirmModal';
import FilterModal from './FilterModal';

const ParentQuery = ({ authUser }) => {
  const [breadcrumbItems] = useState([
    { title: 'Parents', link: '#' },
    { title: 'Parent Queries', link: '#' }
  ]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageLimit: 10,
    totalEntries: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [queryList, setQueryList] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, query: null });
  const [answerModal, setAnswerModal] = useState({ open: false, query: null });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    query: null
  });
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: ''
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    children: '',
    batchId: '',
    startTime: '',
    endTime: ''
  });
  const [filterCount, setFilterCount] = useState(0);
  const [sortingRule, setSortingRule] = useState('createdAt$desc');
  const [refresh, setRefresh] = useState(false);

  const handleRefresh = () => setRefresh((prev) => !prev);

  const tableColumns = [
    { label: 'SL. NO', key: 'srNo' },
    {
      label: 'Parent',
      key: 'parentName',
      render: (row) => (
        <Link to={`/parents?view=${row.parentId}`}>{row.parentName}</Link>
      )
    },
    {
      label: 'Child',
      key: 'childName',
      render: (row) => (
        <Link to={`/children/view/${row.childId}`}>{row.childName}</Link>
      )
    },
    {
      label: 'Batch',
      key: 'batchName',
      render: (row) => (
        <Link to={`/batches/view/${row.batchId}`}>{row.batchName}</Link>
      )
    },
    { label: 'Query', key: 'query' },
    {
      label: 'Status',
      key: 'status',
      render: (row) => (
        <Badge
          style={{ fontWeight: 'bold', fontSize: '12px' }}
          color={
            row.status === 'Published'
              ? 'success'
              : row.status === 'Answered'
              ? 'warning'
              : 'secondary'
          }
        >
          {row.status}
        </Badge>
      )
    },
    { label: 'Asked On', key: 'createdAt' }
  ];

  useEffect(() => {
    if (authUser) {
      handleFetchData(
        pagination.currentPage,
        pagination.pageLimit,
        filters,
        sortingRule
      );
    }
  }, [authUser, filters, sortingRule, refresh]);

  const handleFetchData = async (page, limit, filters = {}, sort_option) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        status: filters?.status || '',
        startTime: filters?.startTime || '',
        endTime: filters?.endTime || '',
        children: filters?.children || '',
        batchId: filters?.batchId || '',
        sort_option
      };
      const response = await fetchParentQueries(params);
      const data = response?.data?.map((query, idx) => {
        let actions = [
          {
            label: 'View',
            onClick: () => setViewModal({ open: true, query }),
            icon: 'eye',
            color: 'primary'
          }
        ];
        if (validatePermissionKey(authUser, ANSWER_PARENT_QUERY)) {
          actions.push({
            label: 'Answer',
            onClick: () => setAnswerModal({ open: true, query }),
            color: 'warning',
            icon: 'edit',
            disabled: query.status === 'Published'
          });
        }
        if (validatePermissionKey(authUser, PUBLISH_PARENT_QUERY)) {
          actions.push({
            label: 'Publish',
            onClick: () => setConfirmModal({ open: true, query }),
            color: 'success',
            icon: 'upload',
            disabled: query.status === 'Published'
          });
        }
        return {
          _id: query._id,
          srNo: idx + 1,
          query: query.query,
          answer: query.answer,
          status: query.status,
          parentName: query.parentName,
          parentId: query.parentId,
          batchName: query.batchName,
          batchId: query.batchId,
          childId: query.childId,
          childName: query.childName,
          createdAt: query.createdAt
            ? moment(query.createdAt).format('DD MMM YYYY')
            : 'N/A',
          actions
        };
      });
      setQueryList(data);
      setPagination({
        currentPage: response?.metadata?.page,
        pageLimit: response?.metadata?.limit,
        totalPages: response?.metadata?.total_pages,
        totalEntries: response?.metadata?.total_results
      });
    } catch (error) {
      console.error('Failed to fetch queries: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    handleFetchData(page, pagination.pageLimit, filters, sortingRule);
  };

  return (
    <div className="page-content">
      <Container fluid>
        {successAlert.show && (
          <SweetAlert
            success
            title="Updated"
            onConfirm={() => setSuccessAlert({ show: false, message: '' })}
          >
            {successAlert.message}
          </SweetAlert>
        )}

        <Breadcrumbs title="Parent Queries" breadcrumbItems={breadcrumbItems} />

        <Row>
          <Col>
            <Card>
              <CardBody>
                {/* OPTIONS */}
                <Row className="mt-2">
                  <Col md="2">
                    <FormGroup>
                      <Label className="col-form-label">Show Entries</Label>
                      <select
                        className="form-control"
                        onChange={(e) => {
                          handleFetchData(
                            1,
                            e.target.value,
                            filters,
                            sortingRule
                          );
                        }}
                        value={pagination.pageLimit}
                        disabled={loading}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label className="col-form-label">Sort Queries</Label>
                      <select
                        className="form-control"
                        onChange={(e) => {
                          setSortingRule(e.target.value);
                        }}
                        value={sortingRule}
                        disabled={loading}
                      >
                        <option value="createdAt$desc">
                          Create Time (Newest First)
                        </option>
                        <option value="createdAt$asc">
                          Create Time (Oldest First)
                        </option>
                      </select>
                    </FormGroup>
                  </Col>
                  <Col className="d-flex justify-content-end align-items-center">
                    <Button
                      size="sm"
                      color="primary"
                      className=" waves-effect waves-light mr-3"
                      onClick={() => setFilterModalOpen(true)}
                    >
                      <i className="ri-filter-fill align-middle mr-2"></i>
                      <span>Filter Queries</span>
                      {filterCount > 0 && (
                        <Badge color="light" pill className="ml-2">
                          {filterCount}
                        </Badge>
                      )}
                    </Button>
                  </Col>
                </Row>
                {/* Table */}
                {loading ? (
                  <div className="text-center">
                    <PageDataLoader height={40} width={40} />
                  </div>
                ) : (
                  <div className="table-rep-plugin">
                    <div
                      className="table-responsive mb-0"
                      data-pattern="priority-columns"
                    >
                      <Table id="tech-companies-1" striped bordered responsive>
                        <thead>
                          <tr>
                            {tableColumns.map((col, index) => (
                              <th key={index}>{col.label}</th>
                            ))}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        {queryList.length === 0 ? (
                          <tbody>
                            <tr>
                              <td
                                colSpan={tableColumns?.length + 1}
                                className="text-center"
                              >
                                No Data Available
                              </td>
                            </tr>
                          </tbody>
                        ) : (
                          <tbody>
                            {queryList?.map((row, index) => (
                              <tr key={index}>
                                {tableColumns?.map((col, idx) => (
                                  <td key={idx}>
                                    {col?.render
                                      ? col?.render(row)
                                      : row[col?.key]}
                                  </td>
                                ))}
                                <td>
                                  <div className="d-flex align-items-center">
                                    {row?.actions?.map((action, index) => (
                                      <Button
                                        key={index}
                                        size="sm"
                                        color={action.color}
                                        className=" waves-effect waves-light mr-2"
                                        onClick={action.onClick}
                                        style={{ whiteSpace: 'nowrap' }}
                                        disabled={action?.disabled ?? false}
                                      >
                                        <i
                                          className={`fas fa-${action.icon} mr-2`}
                                        ></i>
                                        {action.label}
                                      </Button>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        )}
                      </Table>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                <Row className="mt-2">
                  <Col md="5">
                    <p>
                      <strong>
                        {' '}
                        Showing{' '}
                        {(pagination.currentPage - 1) * pagination.pageLimit +
                          1}{' '}
                        to{' '}
                        {Math.min(
                          pagination.currentPage * pagination.pageLimit,
                          pagination.totalEntries
                        )}{' '}
                        of {pagination.totalEntries} entries
                      </strong>
                    </p>
                  </Col>
                  {!loading && queryList.length > 0 && (
                    <Col md="7">
                      <Paginator
                        totalPages={pagination.totalPages}
                        currentPage={pagination.currentPage}
                        changePage={(page) => handlePageChange(page)}
                      />
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {viewModal.open && (
          <ViewQueryModal
            isOpen={viewModal.open}
            handleClose={() => setViewModal({ open: false, query: null })}
            queryData={viewModal.query}
          />
        )}
        {answerModal.open && (
          <AnswerModal
            isOpen={answerModal.open}
            handleClose={() => setAnswerModal({ open: false, query: null })}
            queryData={answerModal.query}
            setSuccessAlert={setSuccessAlert}
            handleRefresh={handleRefresh}
          />
        )}
        {confirmModal.open && (
          <ConfirmModal
            isOpen={confirmModal.open}
            handleClose={() => setConfirmModal({ open: false, query: null })}
            queryData={confirmModal.query}
            setSuccessAlert={setSuccessAlert}
            handleRefresh={handleRefresh}
          />
        )}
        {filterModalOpen && (
          <FilterModal
            isOpen={filterModalOpen}
            handleClose={() => setFilterModalOpen(false)}
            filters={filters}
            setFilters={setFilters}
            setFilterCount={setFilterCount}
          />
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(ParentQuery);
